// PeerProfileOverlay — opens when a teammate's avatar (or name) is tapped
// in any peer-context surface (team roster, team chat, DM panel, pending
// join requests). Shows a read-only profile of that other student so the
// viewer can decide whether to accept their join request, message them, or
// just see who they are working with. Built on top of the HeaderOverlay
// primitive so it shares the same blurred-backdrop / centered-panel /
// ESC-to-close chrome as Activity / Settings / Profile.

import { useEffect, useState } from "react";
import { COLORS } from "../../lib/brand";
import { Image, Linking, Platform, Pressable, Text, View } from "react-native";
import Svg, { Path } from "react-native-svg";

import { HeaderOverlay } from "./HeaderOverlay";
import { getStudentProfile } from "../../lib/studentProfile";
import type { StudentProfileFull } from "../../types/database";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT = COLORS.FOREST.hex;
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.45)";

export type PeerProfileOverlayProps = {
  studentId: string | null;
  onClose: () => void;
  onPressMessage?: (studentId: string) => void;
};

export function PeerProfileOverlay({
  studentId,
  onClose,
  onPressMessage,
}: PeerProfileOverlayProps) {
  const visible = studentId != null;
  const [profile, setProfile] = useState<StudentProfileFull | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!studentId) {
      setProfile(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setProfile(null);
    void (async () => {
      const p = await getStudentProfile(studentId);
      if (!alive) return;
      setProfile(p);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [studentId]);

  const displayName = profile?.full_name ?? "Student";
  const yearLabel = profile?.year_in_school
    ? profile.year_in_school.charAt(0).toUpperCase() + profile.year_in_school.slice(1)
    : null;
  const programLevelLabel = profile?.program_level ?? null;
  const graduationLine = (() => {
    if (!profile) return null;
    if (profile.graduation_term && profile.graduation_year != null) {
      return profile.graduation_term + " " + profile.graduation_year;
    }
    if (profile.graduation_year != null) return "Class of " + profile.graduation_year;
    if (profile.graduation_term) return profile.graduation_term;
    return null;
  })();

  return (
    <HeaderOverlay
      visible={visible}
      onClose={onClose}
      title="Profile"
      subtitle={profile?.full_name ?? null}
    >
      <View style={{ paddingHorizontal: 28, paddingVertical: 24, gap: 18 }}>
        {loading && !profile ? (
          <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 13, color: TEXT_LIGHT }}>
            Loading...
          </Text>
        ) : !profile ? (
          <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 13, color: TEXT_MUTED }}>
            We could not load this profile. They may have removed their account.
          </Text>
        ) : (
          <>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <BigAvatar name={displayName} imageUrl={profile.avatar_url} />
              <View style={{ minWidth: 0, flex: 1, gap: 6 }}>
                <Text
                  style={{
                    fontFamily: "CormorantGaramond_500Medium",
                    fontSize: 26,
                    color: FOREST,
                    lineHeight: 30,
                  }}
                  numberOfLines={2}
                >
                  {displayName}
                </Text>
                {yearLabel || programLevelLabel ? (
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {yearLabel ? <YearPill label={yearLabel} /> : null}
                    {programLevelLabel ? (
                      <Text
                        style={{
                          fontFamily: "DMSans_500Medium",
                          fontSize: 12,
                          color: TEXT_MUTED,
                        }}
                      >
                        {programLevelLabel}
                      </Text>
                    ) : null}
                  </View>
                ) : null}
              </View>
            </View>

            <Section title="Details">
              <View style={{ gap: 10 }}>
                <DetailRow label="College" value={profile.university} />
                <DetailRow label="Major" value={profile.major} />
                <DetailRow label="Graduation" value={graduationLine} />
                {profile.linkedin_url ? (
                  <DetailRow
                    label="LinkedIn"
                    value={profile.linkedin_url}
                    onPress={() => void Linking.openURL(profile.linkedin_url as string)}
                    isLink
                  />
                ) : null}
              </View>
            </Section>

            {profile.bio ? (
              <Section title="About me">
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 14,
                    color: TEXT,
                    lineHeight: 20,
                  }}
                >
                  {profile.bio}
                </Text>
              </Section>
            ) : null}

            {profile.fields_of_competency && profile.fields_of_competency.length > 0 ? (
              <Section title="Strengths">
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  {profile.fields_of_competency.map((f) => (
                    <View
                      key={f}
                      style={{
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 999,
                        borderWidth: 0.5,
                        borderColor: "rgba(28,43,26,0.18)",
                        backgroundColor: "rgba(28,43,26,0.04)",
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: "DMSans_500Medium",
                          fontSize: 12,
                          color: TEXT,
                        }}
                      >
                        {f}
                      </Text>
                    </View>
                  ))}
                </View>
              </Section>
            ) : null}

            {/* Action row — Resume only. Messaging is a roster-row affordance,
                not a profile-overlay one, so we don't surface it here. */}
            {profile.resume_url ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, paddingTop: 6 }}>
                <ActionButton
                  label="View resume"
                  onPress={() => void Linking.openURL(profile.resume_url as string)}
                />
              </View>
            ) : null}
          </>
        )}
      </View>
    </HeaderOverlay>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={{ gap: 6, paddingTop: 6, borderTopWidth: 0.5, borderTopColor: SAND_DARK }}>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1.4,
          color: AMBER,
        }}
      >
        {title.toUpperCase()}
      </Text>
      {children}
    </View>
  );
}

function YearPill({ label }: { label: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: "rgba(181,135,58,0.12)",
        borderWidth: 0.5,
        borderColor: "rgba(181,135,58,0.45)",
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1,
          color: AMBER,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function DetailRow({
  label,
  value,
  onPress,
  isLink = false,
}: {
  label: string;
  value: string | null;
  onPress?: () => void;
  isLink?: boolean;
}) {
  if (!value) {
    return (
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 12 }}>
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 10,
            letterSpacing: 1.4,
            color: TEXT_LIGHT,
            width: 96,
          }}
        >
          {label.toUpperCase()}
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 13,
            color: TEXT_LIGHT,
            fontStyle: "italic",
          }}
        >
          Not set
        </Text>
      </View>
    );
  }
  const valueText = (
    <Text
      style={{
        fontFamily: "DMSans_500Medium",
        fontSize: 13,
        color: isLink ? "#7A5A1F" : TEXT,
        textDecorationLine: isLink ? "underline" : "none",
        flex: 1,
      }}
      numberOfLines={isLink ? 1 : 2}
    >
      {value}
    </Text>
  );
  return (
    <View style={{ flexDirection: "row", alignItems: "baseline", gap: 12 }}>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1.4,
          color: AMBER,
          width: 96,
        }}
      >
        {label.toUpperCase()}
      </Text>
      {onPress ? (
        <Pressable
          onPress={onPress}
          style={[
            { flex: 1 },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          {valueText}
        </Pressable>
      ) : (
        valueText
      )}
    </View>
  );
}

function ActionButton({
  label,
  onPress,
  primary = false,
}: {
  label: string;
  onPress: () => void;
  primary?: boolean;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          paddingHorizontal: 14,
          paddingVertical: 9,
          borderRadius: 6,
          backgroundColor: primary
            ? hovered && isWeb
              ? COLORS.AMBER_DEEP.hex
              : AMBER
            : hovered && isWeb
            ? "rgba(28,43,26,0.04)"
            : "transparent",
          borderWidth: primary ? 0 : 1,
          borderColor: primary ? "transparent" : "rgba(28,43,26,0.25)",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        },
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 11,
          letterSpacing: 1.2,
          color: primary ? FOREST : TEXT,
        }}
      >
        {label.toUpperCase()}
      </Text>
      <Svg width={10} height={10} viewBox="0 0 12 12">
        <Path
          d="M2 6 H10 M7 3 L10 6 L7 9"
          stroke={primary ? FOREST : TEXT}
          strokeWidth={1.6}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Pressable>
  );
}

function BigAvatar({
  name,
  imageUrl,
}: {
  name: string;
  imageUrl: string | null;
}) {
  const dim = 84;
  if (imageUrl) {
    return (
      <Image
        source={{ uri: imageUrl }}
        style={{ width: dim, height: dim, borderRadius: dim / 2 }}
      />
    );
  }
  return (
    <View
      style={{
        width: dim,
        height: dim,
        borderRadius: dim / 2,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: FOREST,
      }}
    >
      <Text
        style={{
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 30,
          color: COLORS.LINEN.hex,
        }}
      >
        {initialsOf(name)}
      </Text>
    </View>
  );
}

function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
